import React from 'react';
import ReactDOM from 'react-dom';
import { IconInfo } from './Icons';
import './MetricInfo.css';

/**
 * The ⓘ next to a metric name: hover or focus it for an explanation.
 * Shared by the Overview's Strategies-vs-Benchmarks table and the Add strategy
 * preview, so both read the same.
 *
 * Uses FIXED positioning, measured on hover, rather than an absolutely
 * positioned child: metric tables sit inside clipping containers (overflow
 * hidden for border radius, overflow-x auto, which clips Y too), so a normal
 * CSS tooltip is cut off. Rendered through a PORTAL to document.body, so an
 * ancestor that gains a transform or filter can't capture it either. No native
 * `title` (that would stack a second browser tooltip).
 */
export default function MetricInfo({ label, text }) {
  const [pos, setPos] = React.useState(null);
  const ref = React.useRef(null);

  const show = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip above when there isn't room below, and keep it on screen at the right
    // edge — the last metric rows sit near the bottom of a long page.
    const below = window.innerHeight - r.bottom > 140;
    setPos({
      left: Math.min(r.left, window.innerWidth - 300),
      top: below ? r.bottom + 8 : undefined,
      bottom: below ? undefined : window.innerHeight - r.top + 8,
    });
  }, []);
  const hide = React.useCallback(() => setPos(null), []);

  if (!text) return null;
  return (
    <span
      ref={ref}
      className="vector-capital-comparison-info"
      aria-label={`${label}: ${text}`}
      role="img"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
    >
      <IconInfo size={13} color="rgba(148, 163, 184, 0.85)" />
      {pos ? ReactDOM.createPortal(
        <span className="vector-capital-comparison-info-tip" role="tooltip" style={pos}>
          {text}
        </span>,
        document.body,
      ) : null}
    </span>
  );
}
